const WORD = /[\p{L}\p{N}'’-]/u

// "**word **" is no bold, and a double-click takes the space after the word: edges stay outside.
export const wrap = (text, rawStart, rawEnd, marker) => {
  let start = rawStart
  let end   = rawEnd
  while (start < end && /\s/.test(text[start]))   start += 1
  while (end > start && /\s/.test(text[end - 1])) end -= 1

  // A drag rarely stops on a word's edge: the format takes in the whole word.
  if (start < end) {
    while (start > 0 && WORD.test(text[start - 1]) && WORD.test(text[start])) start -= 1
    while (end < text.length && WORD.test(text[end - 1]) && WORD.test(text[end])) end += 1
  }

  const before   = text.slice(0, start)
  const selected = text.slice(start, end)
  const after    = text.slice(end)

  if (selected.includes('\n')) {
    const lines = wrapLines(selected, marker)
    return { text: before + lines + after, start, end: start + lines.length }
  }

  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text:  before.slice(0, -marker.length) + selected + after.slice(marker.length),
      start: start - marker.length,
      end:   end - marker.length
    }
  }

  return {
    text:  before + marker + selected + marker + after,
    start: start + marker.length,
    end:   end + marker.length
  }
}

// Emphasis never crosses a line break in Markdown: each line gets its own
// markers, kept inside any list, number or quote prefix.
const LINE = /^(\s*(?:(?:[-*+]|\d+[.)])\s+|>\s?)*)(.*?)(\s*)$/

const wrapLines = (selected, marker) => {
  const parts   = selected.split('\n').map(line => line.match(LINE))
  const filled  = parts.filter(([, , body]) => body !== '')
  const wrapped = body => body.length > marker.length * 2 && body.startsWith(marker) && body.endsWith(marker)
  const remove  = filled.length > 0 && filled.every(([, , body]) => wrapped(body))

  return parts.map(([, prefix, body, trailing]) => {
    if (body === '') return prefix + trailing
    if (remove) return prefix + body.slice(marker.length, -marker.length) + trailing
    return prefix + (wrapped(body) ? body : marker + body + marker) + trailing
  }).join('\n')
}

export const link = (text, start, end) => {
  const selected = text.slice(start, end)
  const isUrl    = /^https?:\/\/\S+$/.test(selected)
  const label    = selected || I18n.t('ticket_assistant.format.link').toLowerCase()
  const url      = isUrl ? selected : 'https://'
  const inserted = `[${label}](${url})`
  const urlStart = start + label.length + 3

  return {
    text:  text.slice(0, start) + inserted + text.slice(end),
    start: isUrl ? start + inserted.length : urlStart,
    end:   isUrl ? start + inserted.length : urlStart + url.length
  }
}

const PREFIXES = {
  ordered: /^\d+\.\s/,
  bullet:  /^[-*]\s/,
  quote:   /^>\s?/
}

// Lists and quotes apply to whole lines, set apart by blank lines: a list run
// straight into the paragraph or list above is read as part of it.
export const block = (text, start, end, kind) => {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1
  const nextBreak = text.indexOf('\n', end)
  const lineEnd   = nextBreak === -1 ? text.length : nextBreak

  const lines  = text.slice(lineStart, lineEnd).split('\n')
  const filled = lines.filter(line => line.trim() !== '')
  const remove = filled.length > 0 && filled.every(line => PREFIXES[kind].test(line))

  let number = 0
  const formatted = lines.map(line => {
    if (line.trim() === '') return line

    const bare = line.replace(PREFIXES.ordered, '').replace(PREFIXES.bullet, '').replace(PREFIXES.quote, '')
    if (remove) return bare

    number += 1
    if (kind === 'ordered') return `${number}. ${bare}`
    if (kind === 'bullet')  return `- ${bare}`
    return `> ${bare}`
  }).join('\n')

  let before = text.slice(0, lineStart)
  let after  = text.slice(lineEnd)

  if (!remove) {
    if (before && !before.endsWith('\n\n')) before = before.replace(/\n?$/, '\n\n')
    if (after && !after.startsWith('\n\n'))  after  = after.replace(/^\n?/, '\n\n')
  }

  return {
    text:  before + formatted + after,
    start: before.length,
    end:   before.length + formatted.length
  }
}
